/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useMemo, useState} from 'react';
import {cloneDeep, merge} from 'lodash';

import {useTheme} from '@salesforce/retail-react-app/app/components/shared/ui';

import {mediaQueryRegistry} from '@amblique/base/app/helpers/media-query-registry';
import {optionsResolver} from '@amblique/base/app/helpers/options-resolver';
import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';

/**
 * React hook for plugin options resolution
 * Wraps optionsResolver with React-specific concerns (hooks, memoization)
 * Automatically handles responsive options if present in resolved options
 *
 * @param {string} pluginName - Plugin name (e.g., 'video', 'swiper')
 * @param {object} localOptions - Local options container (e.g., VIDEO_OPTIONS)
 * @param {string|string[]} presets - Preset name(s)
 * @param {object|string} options - Inline options
 * @param {Function} postProcess - Post-processing callback (receives final resolved options with responsive applied)
 * @returns {object} Final resolved options (with responsive overrides applied)
 *
 * @example Video
 * const options = usePluginOptions('video', VIDEO_OPTIONS, 'hero', { loop: false })
 *
 * @example Swiper
 * const options = usePluginOptions('swiper', SWIPER_OPTIONS, 'products', { slidesPerView: 3 })
 *
 * @example With responsive options
 * const options = usePluginOptions('swiper', SWIPER_OPTIONS, 'products', {
 *   responsive: {
 *     mobile: { slidesPerView: 1 },
 *     tablet: { slidesPerView: 2 },
 *     desktop: { slidesPerView: 4 }
 *   }
 * })
 */
export function usePluginOptions(
  pluginName,
  localOptions,
  presets,
  options,
  postProcess
) {
  const theme = useTheme();
  const serverOptions = useSitePreference(`pluginOptions.${pluginName}`);

  const baseOptions = useMemo(() => {
    const params = {theme, pluginName};
    const mergedContainer = optionsResolver.mergeOptions(localOptions, serverOptions || {}, params);

    return optionsResolver.resolveOptions(mergedContainer, ['default', presets, options], params);
  }, [pluginName, localOptions, serverOptions, presets, options, theme]);

  const [matchedResponsiveOptions, setMatchedResponsiveOptions] = useState(() => {
    if (!baseOptions?.responsive) {
      return {};
    }

    return mediaQueryRegistry.getMatchedValue(baseOptions.responsive) || {};
  });

  useEffect(() => {
    if (!baseOptions?.responsive) {
      setMatchedResponsiveOptions({});

      return;
    }

    // Update immediately when responsive options change
    setMatchedResponsiveOptions(mediaQueryRegistry.getMatchedValue(baseOptions.responsive) || {});

    const unsubscribe = mediaQueryRegistry.subscribe(({value}) => {
      setMatchedResponsiveOptions(value || {});
    }, baseOptions.responsive);

    return unsubscribe;
  }, [baseOptions?.responsive]);

  const resolvedOptions = useMemo(() => {
    if (!baseOptions?.responsive) {
      return baseOptions;
    }

    // eslint-disable-next-line no-unused-vars
    const {responsive, ...baseWithoutResponsive} = baseOptions;

    return merge(cloneDeep(baseWithoutResponsive), matchedResponsiveOptions);
  }, [baseOptions, matchedResponsiveOptions]);

  return useMemo(() => {
    const params = {theme, pluginName};

    return postProcess ? postProcess(resolvedOptions, params) : resolvedOptions;
  }, [resolvedOptions, postProcess, theme, pluginName]);
}
